import Visibility from "@mui/icons-material/Visibility"
import VisibilityOff from "@mui/icons-material/VisibilityOff"
import { BoxProps, IconButton, TextField } from "@mui/material"
import InputAdornment from "@mui/material/InputAdornment"
import React, { useEffect, useState } from "react"
import { useRecoilState } from "recoil"
import { SNACKBAR_TYPES } from "src/modules/const"
import { ShowToast } from "src/states/showToast"
import { Box, Typography } from "src/UILibrary"

type Props = {
  sx?: BoxProps
  name?: string
  placeholder?: string
  required?: boolean
  multiline?: boolean
  text: string
  handleText?: any
  disabled?: boolean
  adornment?: string
  validationMessage?: string
  password?: boolean
  handleFocusOut?: any
  className?: any
  labelStyle?: any
  inputStyle?: any
  description?: string
  readOnly?: boolean
  maxLength?: number
}

const InputTextField = ({
  sx,
  name,
  placeholder,
  required,
  multiline,
  text,
  handleText,
  disabled,
  adornment,
  validationMessage,
  password,
  handleFocusOut,
  className,
  labelStyle,
  inputStyle,
  description,
  readOnly,
  maxLength,
}: Props) => {
  const [, setShowToast] = useRecoilState(ShowToast)

  const [showPassword, setShowPassword] = useState<boolean>(false)

  const placeholderStyle = {
    "& ::placeholder": {
      fontFamily: "Noto Sans JP !important",
    },
    "& ::before": {
      borderBottomColor: "#d4d3d7 !important",
    },
  }

  const style = sx || {}

  useEffect(() => {
    if (maxLength && text.length > maxLength) {
      setShowToast({
        show: true,
        text: maxLength + "文字以下で入力してください。",
        type: SNACKBAR_TYPES.WARNING,
      })
      handleText(text.slice(0, maxLength))
    }
  }, [text])

  return (
    <Box
      className={`input_text ${className}`}
      sx={{
        ...style,
        ...placeholderStyle,
      }}
    >
      {name && (
        <Box display="flex" sx={{ mb: "8px" }}>
          <Typography.InputFieldTitle sx={{ mr: "6px", color: "text.primary", ...labelStyle }}>
            {name}
          </Typography.InputFieldTitle>
          {required && (
            <Box
              sx={{
                px: "8px",
                borderWidth: "1px",
                borderStyle: "solid",
                borderRadius: "4px",
                borderColor: "text.border",
              }}
            >
              <Typography.InputFieldRequired sx={{ m: "auto", color: "text.border" }}>
                必須
              </Typography.InputFieldRequired>
            </Box>
          )}
        </Box>
      )}
      {password ? (
        <TextField
          className="input_text_field"
          variant="standard"
          placeholder={placeholder}
          autoComplete="off"
          sx={{
            fontFamily: "Noto Sans JP !important",
            ...inputStyle,
            mt: 0,
          }}
          type={password && !showPassword ? "password" : "text"}
          value={text}
          onChange={(event) => handleText!(event.target.value)}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={() => setShowPassword(!showPassword)}
                  edge="end"
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      ) : (
        <TextField
          className="input_text_field"
          variant="standard"
          placeholder={placeholder}
          autoComplete="off"
          sx={{
            fontFamily: "Noto Sans JP !important",
            ...inputStyle,
            mt: 0,
          }}
          multiline={multiline}
          minRows={multiline ? 4 : 1}
          maxRows={multiline ? 10 : 1}
          disabled={disabled}
          type={password ? "password" : "text"}
          value={text}
          onChange={(event) => handleText!(event.target.value)}
          onBlur={() => (handleFocusOut ? handleFocusOut() : {})}
          InputProps={{
            endAdornment: (
              <InputAdornment position="start">
                <Typography.InputFieldTitle sx={{ color: "text.primary" }}>
                  {adornment}
                </Typography.InputFieldTitle>
              </InputAdornment>
            ),
            style: { fontSize: 14, fontFamily: "monospace !important" },
            readOnly,
          }}
        />
      )}
      {validationMessage && description ? (
        <>
          <Box sx={{ height: "20px", pt: "5px" }}>
            <Typography.ValidationText>{validationMessage}</Typography.ValidationText>
          </Box>
          <Box sx={{ height: "20px", pt: "5px" }}>
            <Typography.DescriptionText>{description}</Typography.DescriptionText>
          </Box>
        </>
      ) : validationMessage ? (
        <Box sx={{ height: "20px", pt: "5px" }}>
          <Typography.ValidationText>{validationMessage}</Typography.ValidationText>
        </Box>
      ) : description ? (
        <>
          <Box sx={{ height: "20px", pt: "5px" }}>
            <Typography.DescriptionText>{description}</Typography.DescriptionText>
          </Box>
          <Box sx={{ height: "20px", pt: "5px" }}>
            <Typography.DescriptionText></Typography.DescriptionText>
          </Box>
        </>
      ) : (
        <Box sx={{ height: "20px", pt: "5px" }}>
          <Typography.ValidationText></Typography.ValidationText>
        </Box>
      )}
    </Box>
  )
}

export default InputTextField
